import React, { useEffect, useRef, useState } from 'react';
import {
  Table,
  Button,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  Alert,
  Tooltip,
} from 'reactstrap';
import * as Icon from 'react-feather';
import PropTypes from 'prop-types';
import recibosApiService from '../../services/http/recibosApiService';
import useAuth from '../../hooks/useAuth';
import logger from '../../services/logger';
import Loader from '../../layouts/loader/Loader';
import { getSedePortal } from '../../helpers/sedes';
import { submitFormularioRedsys, extraerErrorMensaje } from '../../helpers/redsysForm';

// Los importes pueden llegar como número o como cadena (con punto o coma decimal).
const aNumero = (valor) => Number(typeof valor === 'string' ? valor.replace(',', '.') : valor);

const formatearImporte = (valor) => {
  const numero = aNumero(valor);
  return Number.isNaN(numero) ? '—' : `${numero.toFixed(2)} €`;
};

// "Pendiente" según el servidor (incluye los recibos Redsys "quemados sin
// tarjeta" que el backend considera pagables); si el flag no viene, estado 0.
const esPendiente = (r) => (r.pendiente === undefined ? r.IdEstado === 0 : r.pendiente === true);

// Al alumno no se le muestra la pasarela: los recibos Redsys (idPago 8) salen
// como «Tarjeta».
const formaDePago = (r) =>
  Number(r.idPago) === 8 || /redsys/i.test(r.Forma_de_pago || '') ? 'Tarjeta' : r.Forma_de_pago;

// Solo se puede descargar el recibo una vez cobrado.
const estaCobrado = (r) => r.IdEstado === 1 || r.Estado === 'Cobrado';

const estiloAcciones = { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 };

/**
 * Fila de recibo con la misma disposición que FilaRecibo del admin: celdas
 * atenuadas si el recibo está cobrado o facturado, y acciones centradas con
 * tooltip. En el portal las acciones son Descargar (solo si ya está cobrado) y
 * Pagar (solo Redsys pendiente y pagador alumno, decidido por el servidor con
 * `pagable`).
 */
function FilaReciboPortal({ recibo: r, sede, descargando, onDescargar, onPagar }) {
  const [tooltipRecibo, setTooltipRecibo] = useState(false);
  const [tooltipPagar, setTooltipPagar] = useState(false);
  const isFacturado = r.Num_factura !== 'No facturado';
  const yaCobrado = estaCobrado(r);
  const textClass = yaCobrado || isFacturado ? 'text-black-50' : '';
  const idBajar = `bajarReciboBtn-${r.NUM_Recibo}`;
  const idPagar = `pagarReciboBtn-${r.NUM_Recibo}`;
  const puedePagar = r.pagable === true && !!sede;
  let tooltipPagarTexto = 'No pagable desde el portal';
  if (yaCobrado) tooltipPagarTexto = 'Recibo ya cobrado';
  else if (puedePagar) tooltipPagarTexto = 'Pagar con tarjeta';

  return (
    <tr>
      <td className={textClass}>{r.NUM_Recibo}</td>
      <td className={textClass}>{r.Num_factura}</td>
      <td className={textClass}>{r.Concepto}</td>
      <td className={textClass}>{r.Fecha_Emision}</td>
      <td className={textClass}>{r.Fecha_vencimiento}</td>
      <td className={textClass}>{r.Fecha_cobro || ''}</td>
      <td className={textClass}>{r.Neto}€</td>
      <td className={textClass}>{r.Bruto}€</td>
      <td className={textClass}>{r.descuento ?? 0}%</td>
      <td className={textClass}>{r.Tipo_cliente}</td>
      <td className={textClass}>{formaDePago(r)}</td>
      <td className={textClass}>
        {r.Estado}
        {r.vencido && <small className="d-block text-danger">Vencido</small>}
      </td>
      <td style={estiloAcciones}>
        <span id={idBajar}>
          <Button
            size="sm"
            aria-label="Descargar recibo"
            onClick={() => onDescargar(r)}
            disabled={!yaCobrado || descargando}
          >
            <Icon.Download size={20} className="align-text-center" />
          </Button>
        </span>
        <Tooltip
          placement="top"
          isOpen={tooltipRecibo}
          target={idBajar}
          toggle={() => setTooltipRecibo(!tooltipRecibo)}
        >
          {yaCobrado ? 'Descargar recibo' : 'Disponible cuando el recibo esté pagado'}
        </Tooltip>
        <span id={idPagar}>
          <Button size="sm" aria-label="Pagar" onClick={() => onPagar(r)} disabled={!puedePagar}>
            <Icon.DollarSign size={20} className="align-text-center" />
          </Button>
        </span>
        <Tooltip
          placement="top"
          isOpen={tooltipPagar}
          target={idPagar}
          toggle={() => setTooltipPagar(!tooltipPagar)}
        >
          {tooltipPagarTexto}
        </Tooltip>
      </td>
    </tr>
  );
}

const reciboShape = PropTypes.shape({
  NUM_Recibo: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  Num_factura: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  Concepto: PropTypes.string,
  Fecha_Emision: PropTypes.string,
  Fecha_vencimiento: PropTypes.string,
  Fecha_cobro: PropTypes.string,
  Neto: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  Bruto: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  descuento: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  Tipo_cliente: PropTypes.string,
  Forma_de_pago: PropTypes.string,
  idPago: PropTypes.number,
  IdEstado: PropTypes.number,
  Estado: PropTypes.string,
  nummatricula: PropTypes.number,
  pendiente: PropTypes.bool,
  pagable: PropTypes.bool,
  vencido: PropTypes.bool,
});

FilaReciboPortal.propTypes = {
  recibo: reciboShape.isRequired,
  sede: PropTypes.shape({ terminosId: PropTypes.string }),
  descargando: PropTypes.bool,
  onDescargar: PropTypes.func.isRequired,
  onPagar: PropTypes.func.isRequired,
};

const DatosRecibos = ({ datosRecibos, onRecargar }) => {
  const { getStudent } = useAuth();
  const student = getStudent() || {};
  const headquarter = student.empresaId || '';
  const sede = getSedePortal(headquarter);

  const [modalAbierto, setModalAbierto] = useState(false);
  const [reciboSeleccionado, setReciboSeleccionado] = useState(null);
  const [aceptadoTerminos, setAceptadoTerminos] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [errorDescarga, setErrorDescarga] = useState(null);
  const [descargando, setDescargando] = useState({});
  const [modalTarjeta, setModalTarjeta] = useState(false);
  const [enviandoTarjeta, setEnviandoTarjeta] = useState(false);
  const [errorTarjeta, setErrorTarjeta] = useState(null);
  // Generación del envío en curso de cada modal. Cancelar (o volver con
  // «Atrás») la incrementa: la promesa que siga en vuelo ve otra generación al
  // resolverse y NO manda el formulario al TPV. Es un ref y no un estado para
  // que el handler lea el valor actual y no el de su cierre léxico.
  const envioPagoRef = useRef(0);
  const envioTarjetaRef = useRef(0);

  // Si el alumno vuelve con "Atrás" desde Cyberpac, el navegador restaura la
  // página desde bfcache con el estado intacto: liberamos los modales.
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) {
        envioPagoRef.current += 1;
        envioTarjetaRef.current += 1;
        setEnviando(false);
        setEnviandoTarjeta(false);
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const abrirModalTarjeta = () => {
    setErrorTarjeta(null);
    setModalTarjeta(true);
  };

  // Cancelar también invalida una petición en vuelo: si el servidor contesta
  // después, no se redirige al TPV.
  const cerrarModalTarjeta = () => {
    envioTarjetaRef.current += 1;
    setModalTarjeta(false);
    setEnviandoTarjeta(false);
  };

  // «Cambiar tarjeta»: operación de 0 € en el TPV que tokeniza la nueva
  // tarjeta para los cobros futuros. El backend crea la solicitud para el
  // alumno del JWT; aquí solo se manda idAlumno + sede.
  const handleCambiarTarjeta = async () => {
    if (enviandoTarjeta || !sede) return;
    envioTarjetaRef.current += 1;
    const generacion = envioTarjetaRef.current;
    setEnviandoTarjeta(true);
    setErrorTarjeta(null);
    try {
      const respuesta = await recibosApiService.iniciarCambioTarjeta({
        idAlumno: student.id,
        headquarter,
      });
      if (generacion !== envioTarjetaRef.current) return; // cancelado mientras tanto
      submitFormularioRedsys(respuesta);
    } catch (err) {
      if (generacion !== envioTarjetaRef.current) return;
      logger.error('Error al iniciar el cambio de tarjeta:', err);
      setErrorTarjeta(
        extraerErrorMensaje(err, { porDefecto: 'No se pudo iniciar el cambio de tarjeta.' }),
      );
      setEnviandoTarjeta(false);
    }
  };

  const abrirModalPago = (recibo) => {
    setReciboSeleccionado(recibo);
    setAceptadoTerminos(false);
    setError(null);
    setModalAbierto(true);
  };

  // Siempre se puede cerrar (también mientras "Redirigiendo…", por si la
  // navegación al TPV no llega a producirse).
  const cerrarModalPago = () => {
    envioPagoRef.current += 1;
    setModalAbierto(false);
    setEnviando(false);
  };

  const handlePagar = async () => {
    if (!reciboSeleccionado || !aceptadoTerminos || enviando || !sede) return;
    envioPagoRef.current += 1;
    const generacion = envioPagoRef.current;
    setEnviando(true);
    setError(null);
    try {
      const respuesta = await recibosApiService.iniciarPagoRecibo({
        numRecibo: reciboSeleccionado.NUM_Recibo,
        headquarter,
        acuerdosAceptados: [sede.terminosId],
      });
      if (generacion !== envioPagoRef.current) return; // cancelado mientras tanto
      submitFormularioRedsys(respuesta);
    } catch (err) {
      if (generacion !== envioPagoRef.current) return;
      logger.error('Error al iniciar el pago del recibo:', err);
      setError(extraerErrorMensaje(err));
      setEnviando(false);
    }
  };

  const handleDescargar = async (recibo) => {
    if (!estaCobrado(recibo)) return;
    setErrorDescarga(null);
    // La pestaña se abre DENTRO del gesto del usuario (antes del await): si se
    // abriera tras la respuesta, el bloqueador de ventanas la cortaría sin aviso.
    const ventana = window.open('', '_blank');
    if (!ventana) {
      setErrorDescarga(
        'El navegador ha bloqueado la ventana del recibo. Permite las ventanas emergentes e inténtalo de nuevo.',
      );
      return;
    }
    setDescargando((prev) => ({ ...prev, [recibo.NUM_Recibo]: true }));
    try {
      const data = await recibosApiService.descargarReciboPDF({
        reciboId: recibo.NUM_Recibo,
        headquarter,
      });
      const blob = new Blob([data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      ventana.location = url;
      // Liberamos la URL del blob cuando la pestaña ya ha cargado el PDF.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      ventana.close();
      logger.error('Error al descargar el recibo:', err);
      setErrorDescarga(`No se pudo descargar el recibo ${recibo.NUM_Recibo}.`);
    } finally {
      setDescargando((prev) => ({ ...prev, [recibo.NUM_Recibo]: false }));
    }
  };

  const cargando = datosRecibos === '';
  const errorCarga = datosRecibos === null;
  const recibos = Array.isArray(datosRecibos) ? datosRecibos : [];
  // Igual que el admin: total = suma de netos de todos los recibos. Además, lo
  // que queda por pagar (bruto de los pendientes, que es lo que cobra el TPV).
  const totalRecibos = recibos.reduce((total, r) => total + (aNumero(r.Neto) || 0), 0);
  const importePendiente = recibos
    .filter(esPendiente)
    .reduce((total, r) => total + (aNumero(r.Bruto) || 0), 0);
  // Como el «Cambiar tarjeta Redsys» del admin: solo si el alumno paga por
  // Redsys (algún recibo Redsys como pagador). El backend lo vuelve a exigir.
  const tieneRedsys = recibos.some((r) => Number(r.idPago) === 8 && r.Tipo_cliente === 'A');

  return (
    <TabPane tabId="4">
      {errorDescarga && (
        <Alert color="danger" toggle={() => setErrorDescarga(null)}>
          {errorDescarga}
        </Alert>
      )}

      {onRecargar && (
        <Button className="me-1 mb-2" onClick={onRecargar} disabled={cargando}>
          <Icon.RefreshCw size={16} style={{ marginRight: '8px' }} />
          Actualizar
        </Button>
      )}
      {tieneRedsys && sede && (
        <Button className="me-1 mb-2" onClick={abrirModalTarjeta} disabled={cargando}>
          <Icon.CreditCard size={16} style={{ marginRight: '8px' }} />
          Cambiar tarjeta
        </Button>
      )}

      {cargando && <Loader />}

      {errorCarga && (
        <Alert color="danger" className="mt-2">
          No se pudieron cargar los recibos. Vuelve a intentarlo.
        </Alert>
      )}

      {!cargando && !errorCarga && recibos.length === 0 && <p>No hay recibos disponibles.</p>}

      {!cargando && !errorCarga && recibos.length > 0 && (
        <>
          <Table
            responsive
            borderless
            className="tabla-sin-fondo"
            style={{ fontSize: '13px', borderCollapse: 'separate', borderSpacing: '0 1em' }}
          >
            <thead>
              <tr>
                <th>ID recibo</th>
                <th>ID factura</th>
                <th>Concepto</th>
                <th>Fecha emisión</th>
                <th>Fecha vencimiento</th>
                <th>Fecha cobro</th>
                <th>Neto</th>
                <th>Bruto</th>
                <th>Descuento</th>
                <th>Tipo</th>
                <th>Forma de pago</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {recibos.map((r) => (
                <FilaReciboPortal
                  key={r.NUM_Recibo}
                  recibo={r}
                  sede={sede}
                  descargando={!!descargando[r.NUM_Recibo]}
                  onDescargar={handleDescargar}
                  onPagar={abrirModalPago}
                />
              ))}
            </tbody>
          </Table>
          <span className="text-danger">
            Importe total de los recibos: {totalRecibos.toFixed(2)} €
          </span>
          <span className="ms-3">Pendiente de pago: {importePendiente.toFixed(2)} €</span>
        </>
      )}

      <Modal
        isOpen={modalAbierto}
        toggle={cerrarModalPago}
        onClosed={() => setReciboSeleccionado(null)}
        centered
      >
        {reciboSeleccionado && sede && (
          <>
            <ModalHeader toggle={cerrarModalPago}>
              {`Pagar recibo ${reciboSeleccionado.NUM_Recibo}`}
            </ModalHeader>
            <ModalBody>
              <p className="mb-1">
                <strong>Concepto:</strong> {reciboSeleccionado.Concepto}
              </p>
              <p className="mb-1">
                <strong>Vencimiento:</strong> {reciboSeleccionado.Fecha_vencimiento}
              </p>
              <p className="mb-3">
                <strong>Importe:</strong> {formatearImporte(reciboSeleccionado.Bruto)}
              </p>

              <p className="text-muted small mb-2">
                Antes de continuar, lee el documento de términos y condiciones de contratación de{' '}
                <strong>{sede.nombre}</strong>:
              </p>
              <p className="mb-3">
                <a href={sede.terminosPdf} target="_blank" rel="noopener noreferrer">
                  Ver términos y condiciones de contratación ({sede.nombre})
                </a>
              </p>

              <div className="form-check">
                <Input
                  id="check-terminos-recibo"
                  type="checkbox"
                  className="form-check-input"
                  checked={aceptadoTerminos}
                  disabled={enviando}
                  onChange={(e) => setAceptadoTerminos(e.target.checked)}
                />
                <Label for="check-terminos-recibo" className="form-check-label">
                  He leído y acepto los términos y condiciones de contratación de {sede.nombre}.
                </Label>
              </div>

              <p className="text-muted small mt-3 mb-0">
                Si confirmas el pago, le permitirás a <strong>{sede.razonSocial}</strong> que te
                cargue este pago y futuros pagos conforme a las condiciones estipuladas.
              </p>
              <p className="text-muted small mt-2 mb-0">
                Serás redirigido al entorno seguro de Cyberpac para completar la operación.
              </p>

              {error && (
                <Alert color="danger" className="mt-3 mb-0">
                  {error.mensaje}
                </Alert>
              )}
            </ModalBody>
            <ModalFooter>
              <Button type="button" color="secondary" onClick={cerrarModalPago}>
                Cancelar
              </Button>
              <Button
                type="button"
                color="success"
                onClick={handlePagar}
                disabled={!aceptadoTerminos || enviando}
                className="d-inline-flex align-items-center gap-2"
              >
                <Icon.CreditCard size={16} />
                {enviando
                  ? 'Redirigiendo a Cyberpac…'
                  : `Pagar ${formatearImporte(reciboSeleccionado.Bruto)}`}
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>

      <Modal isOpen={modalTarjeta} toggle={cerrarModalTarjeta} centered>
        {sede && (
          <>
            <ModalHeader toggle={cerrarModalTarjeta}>Cambio de tarjeta</ModalHeader>
            <ModalBody>
              <p className="mb-3">
                Actualiza de forma segura la tarjeta asociada a tus pagos con Cyberpac. La nueva
                tarjeta sustituirá a la actual para los cobros futuros de tus recibos.
              </p>
              <p className="text-muted small mb-2">
                Serás redirigido al entorno seguro de Cyberpac. No se realizará ningún cargo: se
                trata de una operación de verificación de 0&nbsp;€.
              </p>
              <p className="text-muted small mb-0">
                Al continuar, autorizas a <strong>{sede.razonSocial}</strong> a utilizar la nueva
                tarjeta para tus pagos conforme a las condiciones estipuladas.
              </p>
              {errorTarjeta && (
                <Alert color="danger" className="mt-3 mb-0">
                  {errorTarjeta.mensaje}
                </Alert>
              )}
            </ModalBody>
            <ModalFooter>
              <Button type="button" color="secondary" onClick={cerrarModalTarjeta}>
                Cancelar
              </Button>
              <Button
                type="button"
                color="primary"
                onClick={handleCambiarTarjeta}
                disabled={enviandoTarjeta}
                className="d-inline-flex align-items-center gap-2"
              >
                <Icon.CreditCard size={16} />
                {enviandoTarjeta ? 'Redirigiendo a Cyberpac…' : 'Actualizar tarjeta'}
              </Button>
            </ModalFooter>
          </>
        )}
      </Modal>
    </TabPane>
  );
};

DatosRecibos.propTypes = {
  datosRecibos: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(reciboShape)]),
  onRecargar: PropTypes.func,
};

export default DatosRecibos;
